import TWEEN from "@tweenjs/tween.js";
import { Node, PropertyOrKeyValue } from "../../Node";
import { CircleUvBufferGeometry } from "oc-threejs/geometries/CircleUvBufferGeometry";
import * as Maths3 from "oc-threejs/Maths3";
import { OcMaterialHelper } from "oc-threejs/objects/OcMaterialHelper";
import { PNodeRef } from "../../properties/PNodeRef";
import { PNumber } from "../../properties/PNumber";
import { PColor } from "../../properties/PColor";
import { PBoolean } from "../../properties/PBoolean";
import * as Scenes from "oc-threejs/Scenes";
import * as Commons from "oc-commons/Commons";
import { EnvironmentJson } from "../EnvironmentJson";
import { v2, v3 } from "oc-threejs/short3";
import { PEuler } from "../../properties/PEuler";
import { ScreenUtils } from "oc-threejs/ScreenUtils";
import { PString } from "../../properties/PString";
import * as Textures from "oc-threejs/Textures";
import {
  Box3,
  BoxGeometry,
  BufferGeometry,
  Color,
  DoubleSide,
  FogExp2,
  FrontSide,
  GridHelper,
  Group,
  LineSegments,
  Material,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  PolarGridHelper,
  SphereGeometry,
} from "three";

export class EnvironmentNode extends Node {
  parent: EnvironmentJson;
  dayMode: boolean;
  SkyMap: string;
  SkyMapNight: string;
  OtherEnvMap: boolean;
  EnvMap: string;
  SkyY: number;
  GroundMaterial: string;
  FogColor: string;
  FogPower: number;
  GroundY: number;
  GroundRasterMaterial: string;
  GroundRasterMaterialNight: string;
  GroundRasterY: number;
  GroundRasterStep: number;
  GroundRasterRadius: number;
  GroundRasterSegments: number;
  GroundRasterType: string;
  SceneModel: string;
  Farbkalibrierung: boolean;
  Spiegelkugel: boolean;
  SkyDistance: number;
  SkyRotation: [number, number, number];
  sky3d: Mesh;
  ground3d: Object3D;
  sceneModel3d: Object3D;
  groundRaster3d: LineSegments;
  farbkalibrierung3d: Object3D;
  farbkalibrierungplanes3d: Object3D;
  farbkalibrierungplanesDom: Element;
  spiegelkugel3d: Object3D;

  constructor(parent: EnvironmentJson) {
    super();
    this.parent = parent;
    this.dayMode = true;
  }

  get SkyMapTextureId() {
    return this.getTextureVersion(this.dayMode ? this.SkyMap : this.SkyMapNight);
  }

  get EnvMapTextureId() {
    return this.getTextureVersion(this.EnvMap);
  }

  createProperties() {
    return [
      new PNodeRef({
        name: "SkyMap",
        nodeList: (property) => APP.sceneNode.findProperty("EnvMapJson"),
        comment: "die SkyMap definiert den Himmel (und eventuell Boden) der Szene",
        node: this,
      }),
      new PNodeRef({
        name: "SkyMapNight",
        nodeList: (property) => APP.sceneNode.findProperty("EnvMapJson"),
        comment: "die SkyMap definiert den Himmel (und eventuell Boden) der Szene bei Nacht",
        node: this,
      }),
      new PNumber({
        name: "SkyDistance",
        default: 1000,
        min: 0,
        max: 10000,
        decimals: 0,
        comment: "Radius der Skymap",
        showInEditor: (p) => p.node.SkyMap != null,
        node: this,
      }),
      new PNumber({
        name: "SkyY",
        default: 0.0,
        min: -100,
        max: 100,
        decimals: 3,
        comment: "optionaler Y-Versatz des Himmels",
        showInEditor: (p) => p.node.SkyMap != null,
        node: this,
      }),
      new PEuler({
        name: "SkyRotation",
        comment: "optionale Drehung des Himmels",
        showInEditor: (p) => p.node.SkyMap != null,
        node: this,
      }),
      { separator: true },
      new PBoolean({
        name: "OtherEnvMap",
        default: false,
        comment:
          "Normalerweise ist die EnvMap gleich der SkyMap. Wenn man eine abweichende EnvMap wünscht, muss diese Checkbox gesetzt sein.",
        node: this,
      }),
      new PNodeRef({
        name: "EnvMap",
        nodeList: (property) => APP.sceneNode.findProperty("EnvMapJson"),
        comment: "Reflektierende Materialien spiegeln die EnvMap.",
        showInEditor: (p) => p.node.OtherEnvMap,
        node: this,
      }),
      { separator: true },
      new PNodeRef({
        name: "GroundMaterial",
        nodeList: (property) => APP.sceneNode.findProperty("MaterialJson"),
        comment:
          "Wenn gesetzt, dann wird eine Boden-Ebene mit der entsprechenden Bodentextur angezeigt",
        node: this,
      }),
      new PNumber({
        name: "GroundY",
        default: -0.001,
        min: -2,
        max: 2,
        decimals: 3,
        comment: "optionaler Y-Versatz des Bodens",
        showInEditor: (p) => p.node.GroundMaterial != null,
        node: this,
      }),
      { separator: true },
      new PNodeRef({
        name: "GroundRasterMaterial",
        nodeList: (property) => APP.sceneNode.findProperty("MaterialJson"),
        comment:
          "Wenn gesetzt, dann wird ein Bodenraster mit dem entsprechendem Linienmaterial angezeigt",
        node: this,
      }),
      new PNodeRef({
        name: "GroundRasterMaterialNight",
        nodeList: (property) => APP.sceneNode.findProperty("MaterialJson"),
        comment:
          "Wenn gesetzt, dann wird ein Bodenraster mit dem entsprechendem Linienmaterial angezeigt",
        node: this,
      }),
      new PNumber({
        name: "GroundRasterRadius",
        default: 100,
        min: 0,
        max: 10000,
        decimals: 2,
        comment: "Radius des Rasters",
        showInEditor: (p) =>
          p.node.GroundRasterMaterial != null || p.node.GroundRasterMaterialNight != null,
        node: this,
      }),
      new PNumber({
        name: "GroundRasterStep",
        default: 1,
        min: 0.1,
        max: 1000,
        decimals: 2,
        comment: "Abstand der Rasterlinien voneinander",
        showInEditor: (p) =>
          p.node.GroundRasterMaterial != null || p.node.GroundRasterMaterialNight != null,
        node: this,
      }),
      new PNumber({
        name: "GroundRasterSegments",
        default: 16,
        min: 1,
        max: 360,
        decimals: 0,
        comment: "Anzahl der Winkel-Unterteilungen des Rasters",
        showInEditor: (p) =>
          p.node.GroundRasterMaterial != null && p.node.GroundRasterType == "polar",
        node: this,
      }),
      new PNumber({
        name: "GroundRasterY",
        default: 0.01,
        min: -2,
        max: 2,
        decimals: 3,
        comment: "optionaler Y-Versatz des Rasters",
        showInEditor: (p) =>
          p.node.GroundRasterMaterial != null || p.node.GroundRasterMaterialNight != null,
        node: this,
      }),
      new PString({
        name: "GroundRasterType",
        default: "grid",
        selectOptions: ["grid", "polar"],
        comment: "Soll das Raster rechteckig (grid) oder kreisförmig (polar) sein?",
        showInEditor: (p) =>
          p.node.GroundRasterMaterial != null || p.node.GroundRasterMaterialNight != null,
        node: this,
      }),
      { separator: true },
      new PNodeRef({
        name: "SceneModel",
        nodeList: (property) => APP.sceneNode.findProperty("Model"),
        comment:
          "Ein Model wie ein Haus, das als statischer Hintergrund in der Szene angezeigt wird",
        node: this,
      }),
      { separator: true },
      new PColor({
        name: "FogColor",
        comment: "Falls gesetzt, wird Fog verwendet (Nebel hin zum Horizont)",
        node: this,
      }),
      new PNumber({
        name: "FogPower",
        comment:
          "Wie stark ist der Fog-Effekt? Typische Werte gehen von 0.0001 bis 0.1, default ist 0.01",
        default: 0.00025,
        step: 0.00005,
        min: 0,
        max: 0.01,
        decimals: 5,
        showInEditor: (p) => p.node.FogColor != null,
        node: this,
      }),
      { separator: true },
      new PBoolean({
        name: "Farbkalibrierung",
        default: false,
        comment:
          "zeigt eine Tabelle mit Materialkugeln in der Szene an, die zum Prüfen der Lichteinstellungen dient. Die Kugeln der jeweilste oberste Zeile der Quadranten sollten farblich der direkt darunterliegenden Kugel möglichst nahe kommen.",
        node: this,
      }),
      new PBoolean({
        name: "Spiegelkugel",
        default: false,
        comment:
          "zeigt eine große spiegelnde Kugel an, die über einem weißen Podest schwebt, um Schatten, Licht und EnvMap zu testen.",
        node: this,
      }),
    ] as PropertyOrKeyValue[];
  }

  init() {
    super.init();
    this.updateScene();
    return this;
  }

  updateScene() {
    this._dataChanged = {};
    this._updateSky();
    this._updateEnvMap();
    this._updateGround();
    this._updateGroundRaster();
    this._updateSceneModel();
    this._updateFog();
    this._updateHelper();
  }

  getTextureVersion(envMap) {
    const envMapNode = APP.sceneNode.getEnvMapNode(envMap);
    return envMapNode?.texture3d?.image ? envMapNode?.imageVersions : -1;
  }

  toggleDayNight() {
    this.dayMode = !this.dayMode;
  }

  _updateSky() {
    if (
      !(
        this.changed("SkyMap") ||
        this.changed("SkyMapTextureId") ||
        this.changed("SkyDistance") ||
        this.changed("SkyRotation") ||
        this.changed("SkyY") ||
        this.changed("dayMode")
      )
    ) {
      return;
    }
    const oldSky = this.sky3d;
    this.sky3d = null;
    const texture = APP.sceneNode.getEnvMapNode(this.dayMode ? this.SkyMap : this.SkyMapNight);
    if (texture?.texture3d?.image) {
      const skyMaterial = new MeshBasicMaterial({
        map: texture.texture3d,
        side: FrontSide,
        fog: false,
        transparent: true,
        opacity: 1,
      });
      const radius = this.getSkyDistance();
      const geometry = texture.isEuirectangular
        ? new SphereGeometry(radius, 48, 24)
        : new BoxGeometry(radius * 2, radius * 2, radius * 2);
      // ohne die Scale z=-1 wäre die EnvMap vs. SkyMap invertiert
      geometry.scale(1, 1, -1);
      const sky = new Mesh(geometry, skyMaterial);
      sky.name = "sky";
      sky.raycastVisibleTags = [];
      sky.frustumCulled = false;
      sky.position.setY(this.SkyY);
      sky.rotation.fromArray(this.SkyRotation);
      (sky as any).isSceneBackground = true;
      this.sky3d = sky;
    }
    const newSky = this.sky3d;
    this._swapMaybeAnimated(newSky, oldSky, () =>
      APP.sceneNode.triggers.add("SceneBackgroundChange"),
    );
  }

  _swapMaybeAnimated(
    newObject: Object3D & { material: Material | Material[] },
    oldObject: Object3D & { material: Material | Material[] },
    onComplete = () => {},
  ) {
    if (newObject && oldObject) {
      const newMaterials = Array.isArray(newObject.material)
        ? newObject.material
        : [newObject.material];
      const oldMaterials = Array.isArray(oldObject.material)
        ? oldObject.material
        : [oldObject.material];
      newMaterials.forEach((m) => (m.opacity = 0));
      APP.scene.add(newObject);
      const tween = new TWEEN.Tween({ oldO: 1, newO: 0 });
      tween.to({ oldO: 0, newO: 1 });
      tween.onUpdate(({ oldO, newO }) => {
        oldMaterials.forEach((m) => (m.opacity = oldO));
        newMaterials.forEach((m) => (m.opacity = newO));
        STAGE.dirty();
      });
      tween.start(STAGE.tweenStartNowTime);
      tween.onComplete(() => {
        Scenes.remove(oldObject);
        onComplete();
      });
    } else if (newObject) {
      APP.scene.add(newObject);
      onComplete();
    } else if (oldObject) {
      Scenes.remove(oldObject);
      onComplete();
    }
  }

  _updateEnvMap() {
    if (
      !(
        this.changed("EnvMap") ||
        this.changed("EnvMapTextureId") ||
        this.changed("OtherEnvMap") ||
        this.changed("SkyMap") ||
        this.changed("SkyMapTextureId") ||
        this.changed("dayMode")
      )
    ) {
      return;
    }
    const map = this.dayMode ? (this.OtherEnvMap ? this.EnvMap : this.SkyMap) : null;
    const texture3d = APP.sceneNode.getEnvMapNode(map)?.texture3d;
    STAGE.scene.environment = texture3d;
  }

  _updateGround() {
    if (
      !(
        this.changed("GroundMaterial") ||
        this.changed("SkyDistance") ||
        this.changed("GroundY") ||
        this.changed("Shadow")
      )
    ) {
      return;
    }
    Scenes.remove(this.ground3d);
    this.ground3d = null;
    if (!Commons.isEmpty(this.GroundMaterial)) {
      const material = APP.sceneNode.getMaterial(this.GroundMaterial);
      const radius = this.getSkyDistance();
      const geometry = new CircleUvBufferGeometry(radius, 48);
      const ground = new Mesh(geometry, material);
      ground.name = "ground";
      ground.raycastVisibleTags = [];
      ground.quaternion.setFromUnitVectors(Maths3.vector.z, Maths3.vector.y);
      ground.position.setY(this.GroundY);
      ground.receiveShadow = this.parent.rendererNode.ShadowsEnabled;
      (ground as any).isSceneBackground = true;
      APP.scene.add(ground);
      this.ground3d = ground;
      APP.sceneNode.triggers.add("SceneBackgroundChange");
    }
  }

  _updateGroundRaster() {
    if (
      !(
        this.changed("GroundRasterMaterial") ||
        this.changed("GroundRasterY") ||
        this.changed("GroundRasterStep") ||
        this.changed("GroundRasterSegments") ||
        this.changed("GroundRasterRadius") ||
        this.changed("GroundRasterType") ||
        this.changed("dayMode")
      )
    ) {
      return;
    }
    const oldRaster = this.groundRaster3d;
    this.groundRaster3d = null;
    const onlyPositionGeometry = (line) => {
      const geometry = new BufferGeometry();
      const position = line.geometry.getAttribute("position").clone();
      geometry.setAttribute("position", position);
      return geometry;
    };
    const gridGeometry = () => {
      return onlyPositionGeometry(
        new GridHelper(this.GroundRasterRadius, this.GroundRasterRadius / this.GroundRasterStep),
      );
    };
    const polarGeometry = () => {
      return onlyPositionGeometry(
        new PolarGridHelper(
          this.GroundRasterRadius,
          this.GroundRasterSegments,
          this.GroundRasterRadius / this.GroundRasterStep,
        ),
      );
    };
    const material =
      this.GroundRasterMaterial &&
      APP.sceneNode.getMaterial(
        this.dayMode ? this.GroundRasterMaterial : this.GroundRasterMaterialNight,
      );
    if (material) {
      const geometry = this.GroundRasterType == "grid" ? gridGeometry() : polarGeometry();
      material.transparent = true;
      material.opacity = 1;
      this.groundRaster3d = new LineSegments(geometry, material);
      this.groundRaster3d.name = "groundRaster";
      (this.groundRaster3d as any).isSceneBackground = true;
      this.groundRaster3d.position.y = this.GroundRasterY;
      // this.groundRaster3d.renderOrder = 3;
    }
    const newRaster = this.groundRaster3d;
    this._swapMaybeAnimated(newRaster, oldRaster);
  }

  _updateSceneModel() {
    if (!this.changed("SceneModel")) {
      return;
    }
    APP.sceneNode.loadModelPromise(this.SceneModel).then((model) => {
      Scenes.remove(this.sceneModel3d);
      this.sceneModel3d = model;
      if (model) {
        APP.scene.add(model);
        model.isSceneBackground = true;
      }
      APP.sceneNode.triggers.add("SceneBackgroundChange");
    });
  }

  _updateFog() {
    if (!(this.changed("FogColor") || this.changed("FogPower"))) {
      return;
    }
    if (this.FogColor) {
      APP.scene.fog = new FogExp2(new Color(this.FogColor), this.FogPower);
    } else {
      APP.scene.fog = null;
    }
  }

  _updateHelper() {
    if (this.changed("Farbkalibrierung")) {
      Scenes.remove(this.farbkalibrierung3d);
      this.farbkalibrierung3d = null;

      Scenes.remove(this.farbkalibrierungplanes3d);
      this.farbkalibrierungplanes3d = null;

      this.farbkalibrierungplanesDom?.parentNode?.removeChild(this.farbkalibrierungplanesDom);
      this.farbkalibrierungplanesDom = null;

      if (this.Farbkalibrierung) {
        this.createFarbkalibrierung3d();
      }
    }
    if (this.changed("Spiegelkugel")) {
      Scenes.remove(this.spiegelkugel3d);
      this.spiegelkugel3d = null;
      if (this.Spiegelkugel) {
        this.createSpiegelkugel3d();
      }
    }
  }

  createSpiegelkugel3d() {
    this.spiegelkugel3d = new Group();
    this.spiegelkugel3d.name = "Spiegelkugel";
    APP.scene.add(this.spiegelkugel3d);

    const sphere = new Mesh(
      new SphereGeometry(6, 30, 30),
      new MeshStandardMaterial({ roughness: 0, metalness: 1, color: 0xffffff }),
    );
    sphere.castShadow = true;
    sphere.receiveShadow = true;
    sphere.position.y += 8;
    this.spiegelkugel3d.add(sphere);

    for (let i = 0; i < 10; i++) {
      const sockel = new Mesh(
        new BoxGeometry(30 - i * 2, 30 - i * 2, 0.1),
        new MeshStandardMaterial({
          roughness: 1,
          metalness: 0,
          color: 0xffffff,
        }),
      );
      sockel.rotation.x = Math.PI / 2;
      sockel.castShadow = true;
      sockel.receiveShadow = true;
      sockel.position.y += i * 0.1;
      this.spiegelkugel3d.add(sockel);
    }
  }

  getSkyDistance() {
    let distance = this.SkyDistance;
    if (distance > APP.camera.far * 0.95) {
      console.warn("SkyDistance adjusted because it was far away than camera.far");
      distance = APP.camera.far * 0.9;
    }
    return distance;
  }

  getName() {
    return "Environment";
  }

  private createFarbkalibrierung3d() {
    this.farbkalibrierung3d = new OcMaterialHelper(2);
    this.farbkalibrierung3d.name = "Farbkalibrierung";
    APP.scene.add(this.farbkalibrierung3d);
  }

  private createFarbkalibrierungsPlanes3d() {
    this.farbkalibrierungplanes3d = new Group();
    this.farbkalibrierungplanes3d.name = "FarbkalibrierungPlanes3d";
    APP.camera.add(this.farbkalibrierungplanes3d);

    // erzeugt werden Quadrate mit jeweils einer Farbe
    // der rechte Teil des Quadrats ist ein DOM-Element, das die Farbe
    // unverfälscht von Three anzeigt
    // der linke Teil besteht aus zwei Quadraten, die innerhalb der Szene sind,
    // aber direkt vor der Kamera schweben. Beide Quadrate sind von den Render
    // Settings abhängig. Das obere Quadrat ist ein MeshStandardMaterial und
    // reagiert auf Licht, das untere Quadrat ist ein MeshBasicMaterial und
    // reagiert nicht auf Licht

    const createColorLeftSide3d = (colorStr, domRect) => {
      const screenUtils = new ScreenUtils();
      const cameraDistance = 1;
      const [topleft, bottomright] = [
        v2(domRect.left, domRect.top),
        v2(domRect.right + 2, domRect.bottom),
      ].map((v) => screenUtils.screenToCameraCoordinates(Maths3.v2To3(v, cameraDistance)));
      const boxOnScreen = new Box3().setFromPoints([topleft, bottomright]);
      const size = boxOnScreen.getSize(v3());
      const n = 4;
      size.y /= n;
      const color = new Color(colorStr);
      // const geometry = new THREE.PlaneGeometry(size.x, size.y);
      const geometry = new BoxGeometry(size.x, size.x * 0.001, size.y);
      const createColorPlane = (material, position) => {
        const plane = new Mesh(geometry, material);
        plane.position.copy(position);
        plane.rotation.x = (90 * Math.PI) / 180;
        plane.name = colorStr;
        return plane;
      };
      const colorMap = Textures.createSingleColorTexture(colorStr, 10);
      let i = 0;
      const y = (i) => (n - i - 0.5) / n;
      [
        createColorPlane(
          new MeshBasicMaterial({ color, side: DoubleSide }),
          boxOnScreen.grid(0.5, y(i++), 0),
        ),
        createColorPlane(
          new MeshBasicMaterial({ map: colorMap, side: DoubleSide }),
          boxOnScreen.grid(0.5, y(i++), 0),
        ),
        createColorPlane(new MeshStandardMaterial({ color }), boxOnScreen.grid(0.5, y(i++), 0)),
        createColorPlane(
          new MeshStandardMaterial({ map: colorMap }),
          boxOnScreen.grid(0.5, y(i++), 0),
        ),
      ].forEach((p) => this.farbkalibrierungplanes3d?.add(p));
    };

    const createDiv = (className) => {
      const div = document.createElement("div");
      div.setAttribute("class", className);
      return div;
    };

    this.farbkalibrierungplanesDom = createDiv("farbkalibrierungplanes");
    const farbrows = createDiv("farbrows");
    this.farbkalibrierungplanesDom.appendChild(farbrows);
    const colorCallbacks = [];

    const createColorPlane = (colorStr) => {
      const quadratDiv = createDiv("quadrat");
      const leftDiv = createDiv("left-half");
      quadratDiv.appendChild(leftDiv);
      const rightDiv = createDiv("right-half");
      rightDiv.setAttribute("style", `background-color: ${colorStr}`);
      quadratDiv.appendChild(rightDiv);
      farbrows.appendChild(quadratDiv);
      colorCallbacks.push(() => createColorLeftSide3d(colorStr, leftDiv.getBoundingClientRect()));
    };

    const factorRange = (n) => _.range(n).map((a, i, array) => i / (array.length - 1));
    const fixed = (a, b, t) => (a * (1 - t) + b * t).toFixed(0);
    const grayshade = (t) => `hsl(0,0%,${fixed(0, 100, t)}%)`;
    const colorshade = (t) => `hsl(${fixed(0, 360, t)},${fixed(100, 60, t)}%,${fixed(50, 90, t)}%)`;

    const n = 11;
    // gray shades
    factorRange(n).forEach((t) => createColorPlane(grayshade(t)));
    // colors
    factorRange(n).forEach((t) => createColorPlane(colorshade(t)));

    const createColorPlanes3d = () => colorCallbacks.forEach((c) => c());
    const sceneEditorWrapper = document.querySelector(".scene-editor-app-wrapper");
    sceneEditorWrapper.appendChild(this.farbkalibrierungplanesDom);

    Commons.sleepUntil(() => this.farbkalibrierungplanesDom.isConnected).then(createColorPlanes3d);

    STAGE.on("setRenderSize", () => {
      Scenes.empty(this.farbkalibrierungplanes3d);
      createColorPlanes3d();
    });

    STAGE.dirty();
  }
}
